import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plane, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const { user, login, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!ready) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(`Bienvenido`);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo iniciar sesión";
      toast.error(typeof msg === "string" ? msg : "Error de autenticación");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain-bg" style={{ background: "var(--bg)" }}>
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12" style={{ background: "var(--sidebar)" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
            <Plane className="text-white" size={20} strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-white font-bold tracking-tight text-lg">Voyager</div>
            <div className="text-[11px] uppercase tracking-widest text-sky-300">Cotizador</div>
          </div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-300 mb-3">Panel de agencia</p>
          <h1 className="font-serif-display text-5xl xl:text-6xl font-extrabold tracking-tight text-white leading-tight">
            Cotizaciones que enamoran, en segundos.
          </h1>
          <p className="text-slate-300 mt-5 max-w-md text-base leading-relaxed">
            Crea PDFs profesionales, compartí links con tus pasajeros y recibí confirmaciones en tiempo real.
          </p>
        </div>
        <p className="text-xs text-slate-400 italic">"Sujeto a disponibilidad a la hora de confirmación"</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-xl bg-sky-600 flex items-center justify-center">
                <Plane className="text-white" size={16} strokeWidth={2.4} />
              </div>
              <span className="font-bold text-slate-900 text-lg">Voyager</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-2">Ingresar</p>
            <h2 className="font-serif-display text-4xl font-extrabold tracking-tight text-slate-900">Bienvenido de vuelta</h2>
            <p className="text-slate-600 mt-2">Iniciá sesión con tu cuenta de agente.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@voyager.com"
                autoFocus
                required
                className="rounded-xl h-12"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Contraseña</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={4}
                  placeholder="••••••••"
                  className="rounded-xl h-12 pl-9"
                  data-testid="input-password"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-base shadow-xl shadow-slate-900/10"
              data-testid="button-login"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
              Iniciar sesión
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
